import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HomeWrap } from "./HomeWrap";
import { HomeLink } from "./HomeLink";
import { CityWaitlistCard } from "@/components/browse/CityWaitlistCard";
import { LocationSearchInput } from "@/components/LocationSearchInput";
import type { HomeCounts } from "@/hooks/useHomeCounts";
import { useHomeCityCoverage } from "@/hooks/useHomeCityCoverage";
import { useAreaCoverageProbe } from "@/hooks/useAreaCoverageProbe";
import { markGuestBrowse } from "@/lib/guestBrowsing";

interface Props {
  counts: HomeCounts | null;
}

type PickedCity = { description: string; lat: number; lng: number };

/**
 * Hand the chosen place to Browse the way every other mount does: Browse reads
 * `grounded_browse_location` once, in the initialiser of its location filter
 * (useBrowseFilters.ts:141). Writing it here and navigating is the existing
 * mechanism, not a new one.
 */
function openBrowseAt(city: PickedCity) {
  try {
    localStorage.setItem(
      "grounded_browse_location",
      JSON.stringify({ ...city, source: "area" }),
    );
  } catch {
    // Private mode or blocked storage: Browse still opens, just unfiltered.
  }
}

export function HomeCoverage({ counts }: Props) {
  const { data: cities = [] } = useHomeCityCoverage();
  const [picked, setPicked] = useState<PickedCity | null>(null);
  const navigate = useNavigate();

  // The same coverage probe Browse gates its waitlist on (Browse.tsx:833).
  // Without it this section told people we were not in Santa Monica while
  // listing three scored Santa Monica cafés directly above, and put a vote
  // button for a covered city one click away.
  const { hasCoverageNearby, isChecking } = useAreaCoverageProbe({
    lat: picked?.lat,
    lng: picked?.lng,
    enabled: !!picked,
  });

  // Deliberately `=== false`, not falsy. The probe reports `null` both while it
  // is running and when it errors, and neither is evidence that we do not cover
  // somewhere. Claiming a city is uncovered is the expensive mistake: it is
  // wrong on screen and it writes a row.
  const covered = picked !== null && hasCoverageNearby === true;
  const uncovered = picked !== null && hasCoverageNearby === false;

  // In an effect, not in render: navigating during render warns and can fire
  // twice under StrictMode.
  useEffect(() => {
    if (!covered || !picked) return;
    markGuestBrowse();
    openBrowseAt(picked);
    navigate("/browse");
  }, [covered, picked, navigate]);

  const metros = [
    { name: "Los Angeles", count: counts?.los_angeles },
    { name: "Orange County", count: counts?.orange_county },
    { name: "San Diego", count: counts?.san_diego },
    { name: "Long Beach", count: counts?.long_beach },
  ];

  return (
    <section id="cities" className="scroll-mt-20 py-[88px] max-[820px]:py-[60px]">
      <HomeWrap>
        <h2 className="max-w-[20ch] font-display text-[clamp(30px,3.6vw,42px)] font-semibold leading-[1.06] tracking-[-0.015em] text-[#3B2314]">
          {counts
            ? `${counts.los_angeles} cafés in Los Angeles alone`
            : "Cafés in Los Angeles, Orange County, San Diego and Long Beach"}
        </h2>
        <p className="mt-[18px] max-w-[52ch] text-[17px] leading-[1.6] text-[#7B4F2E]">
          We're deepest in Southern California and still adding. If the café you work from isn't on
          here yet, you can suggest it.
        </p>

        <dl className="mt-11 grid grid-cols-2 gap-7 border-y border-[#E2D5C0] py-7 min-[760px]:grid-cols-4">
          {metros.map((m) => (
            <div key={m.name}>
              <dd className="font-display text-[44px] font-semibold leading-none tracking-[-.02em] text-[#3B2314]">
                {typeof m.count === "number" ? m.count : null}
              </dd>
              <dt className="mt-[7px] text-sm text-[#8A7563]">{m.name}</dt>
            </div>
          ))}
        </dl>

        {/* The city pages, with real cafés and their real scores on them. */}
        {cities.length > 0 && (
          <div className="mt-10 grid gap-5 min-[800px]:grid-cols-3">
            {cities.map((c) => (
              <HomeLink
                key={c.slug}
                to={`/work-cafes/${c.slug}`}
                className="block rounded-[14px] border border-[#E4D8C6] bg-white px-[22px] pb-5 pt-[22px] no-underline transition-colors hover:bg-[#FDFBF8]"
              >
                <h3 className="mb-3 font-display text-[22px] font-semibold tracking-[-0.015em] text-[#3B2314]">
                  {c.displayName}
                </h3>
                <ul className="m-0 list-none p-0 text-[15px] text-[#7B4F2E]">
                  {c.cafes.map((cafe) => (
                    <li
                      key={cafe.name}
                      className="flex items-baseline justify-between gap-3 border-b border-[#F0E7D8] py-[7px] last:border-b-0"
                    >
                      <span className="min-w-0 truncate">{cafe.name}</span>
                      {/* Real score or nothing. A café without one shows its
                          name alone rather than a number that would read as
                          fact. */}
                      {cafe.score !== null && (
                        <b className="font-display text-[17px] font-semibold text-[#3B2314]">
                          {cafe.score}
                        </b>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-[14px] text-sm font-semibold text-[#3B2314]">See the list →</div>
              </HomeLink>
            ))}
          </div>
        )}

        {/* Outside the coverage area there was previously nothing to do. The
            waitlist already existed but only inside /browse. Same component,
            same anonymous insert, same rate limit. */}
        <div className="mt-9 max-w-2xl">
          {uncovered ? (
            <div>
              <CityWaitlistCard source="home" city={picked.description} lat={picked.lat} lng={picked.lng} />
              <button
                type="button"
                onClick={() => setPicked(null)}
                className="mt-3 text-sm font-semibold text-[#7B4F2E] underline underline-offset-2"
              >
                Choose somewhere else
              </button>
            </div>
          ) : picked ? (
            // Probe still running, or it failed. Either way this is not the
            // moment to tell someone we are not in their city.
            <div className="rounded-[14px] border border-[#E4D8C6] bg-white p-6">
              <p className="text-base leading-[1.6] text-[#7B4F2E]" aria-live="polite">
                {isChecking
                  ? `Checking ${picked.description}...`
                  : `We could not check ${picked.description} just now.`}
              </p>
              <button
                type="button"
                onClick={() => setPicked(null)}
                className="mt-3 text-sm font-semibold text-[#3B2314] underline underline-offset-2"
              >
                Choose somewhere else
              </button>
            </div>
          ) : (
            <div className="rounded-[14px] border border-[#E4D8C6] bg-white p-6">
              <h3 className="font-display text-[21px] font-semibold tracking-[-0.015em] text-[#3B2314]">
                Somewhere else?
              </h3>
              <p className="mt-2 max-w-[52ch] text-base leading-[1.6] text-[#7B4F2E]">
                Tell us where you are. It counts toward the next city we score.
              </p>
              <div className="mt-4">
                <LocationSearchInput
                  onLocationSelect={(loc) => {
                    if (loc) setPicked(loc);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </HomeWrap>
    </section>
  );
}
